import { useState, useEffect } from 'react';
import { CaretDown, CaretUp } from '@phosphor-icons/react';
import { useLocation } from '../../lib/routing';
import { Link } from '../../lib/routing';
import { useLocalStorage } from '../../hooks/use-local-storage';
import { api } from '../../lib/api';

interface ClassroomItem {
  classroom_id: string;
  classroom_teacher_id: string;
  name: string;
  level: string | null;
  subject: string | null;
  student_count: number;
}

export function RailClassList({ onNav }: { onNav: () => void }) {
  const location = useLocation();
  const [open, setOpen] = useLocalStorage<boolean>('naia.rail.classes.open', true);
  const [classes, setClasses] = useState<ClassroomItem[]>([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    api.onboarding.status.query()
      .then((s) => {
        if (cancelled) return;
        /* Une ligne par (classe, matière enseignée) — un prof qui enseigne
           plusieurs matières dans la même classe la reçoit plusieurs fois.
           `subject` sert à d'autres écrans (onboarding, fiche classe) : le
           dédoublonnage reste ici, pas dans getOnboardingStatus. */
        const byClassroom = new Map<string, ClassroomItem>();
        for (const cls of s?.classrooms ?? []) {
          if (!byClassroom.has(cls.classroom_id)) byClassroom.set(cls.classroom_id, cls);
        }
        setClasses([...byClassroom.values()]);
      })
      .catch(() => {})
      .finally(() => { if (!cancelled) setLoaded(true); });
    return () => { cancelled = true; };
  }, []);

  if (!loaded) return null;

  return (
    <div className="naia-rail__body shrink-0 flex flex-col mt-1.5">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-2 w-full px-2.5 min-h-[44px] bg-transparent border-none rounded-[9px] cursor-pointer font-[inherit] text-[var(--rail-text)] hover:bg-[var(--rail-surface-hover)] transition-colors"
      >
        <span aria-hidden className="naia-item__icon is-w22">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.7} strokeLinecap="round" strokeLinejoin="round" aria-hidden>
            <path d="M12 2.5 22 8 12 13.5 2 8z" />
            <path d="M5 10v6.5c0 1.5 3.1 3.5 7 3.5s7-2 7-3.5V10" />
            <path d="M22 8v7" />
          </svg>
        </span>
        <span className="naia-rail__label flex-1 font-bold text-[13px] text-left text-[var(--rail-text-active)]">Mes classes</span>
        <span className="naia-rail__label text-[var(--rail-text-muted)]" aria-hidden>
          {open ? <CaretUp size={14} weight="bold" /> : <CaretDown size={14} weight="bold" />}
        </span>
      </button>

      {open && classes.length === 0 && (
        <p className="naia-rail__label my-0.5 px-2.5 py-1.5 text-[12px] text-[var(--rail-text-muted)]">Aucune classe pour l'instant</p>
      )}

      {open && (
        <div className="flex flex-col gap-px pl-1.5">
          {classes.map((cls) => {
            const href = `/teacher/classes/${cls.classroom_id}`;
            const active = location.pathname === href;
            /* cls.level vient de classrooms.level, colonne texte morte et vide
               sur toutes les classes : le repli sur le premier mot est donc le
               SEUL chemin, et il recopie le nom entier quand il n'a pas
               d'espace. On affiche quand meme le badge dans ce cas pour garder
               un format uniforme entre toutes les lignes de la liste. */
            const badge = cls.level ?? cls.name.split(' ')[0] ?? '?';
            const label = cls.name || `Classe ${badge}`;
            const showBadge = badge.trim().length > 0;
            return (
              <Link
                key={cls.classroom_id}
                to={href}
                className="group flex items-center gap-2 px-2.5 py-[5px] min-h-[44px] rounded-lg text-[var(--rail-text)] no-underline font-medium text-[13px] transition-colors hover:bg-[var(--rail-surface-hover)] hover:text-[var(--rail-text-active)] aria-[current=page]:text-[var(--rail-text-active)] aria-[current=page]:bg-[var(--rail-active)] aria-[current=page]:font-bold"
                onClick={onNav}
                aria-current={active ? 'page' : undefined}
              >
                {showBadge && (
                  <span aria-hidden className="naia-classbadge shrink-0 w-[26px] h-[26px] rounded-[7px] bg-[var(--rail-surface)] text-[var(--rail-text-active)] flex items-center justify-center text-[9.5px] font-extrabold border border-[var(--rail-line)] group-aria-[current=page]:bg-[var(--rail-active)] group-aria-[current=page]:text-[var(--rail-text-active)]">{badge}</span>
                )}
                <span className="naia-rail__label whitespace-nowrap overflow-hidden text-ellipsis">{label}</span>
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}
